import React, { useState, useEffect } from "react";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { parse, isAfter, isBefore, isEqual, format } from "date-fns";
import { connect } from "react-redux";
import {
  getAvailabilityRequest,
  saveAvailabilityRequest,
} from "../../../Departments/DepartmentsApiActions";

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const ClinicTiming = ({
  clinicId = "",
  doctorId = "",
  availability,
  getAvailability,
  saveAvailability,
  mode,
}) => {
  // State: for each day an array of intervals [{ startTime: Date, endTime: Date, slotType: Boolean }]
  const [hours, setHours] = useState(() => {
    const init = {};
    daysOfWeek.forEach((day) => {
      init[day] = [{ startTime: null, endTime: null, slotType: false }]; // Start with one interval per day
    });
    return init;
  });

  useEffect(() => {
    // Validate closing > starting time
    ValidatorForm.addValidationRule("isAfterStart", (value) => {
      const parsedEnd = parse(value, "h:mm aa", new Date());

      for (let day in hours) {
        for (let interval of hours[day]) {
          if (
            interval.endTime &&
            format(interval.endTime, "h:mm aa") === value
          ) {
            if (!interval.startTime) return true;
            return isAfter(parsedEnd, interval.startTime);
          }
        }
      }

      return true;
    });

    // Validate intervals for a day do not overlap
    ValidatorForm.addValidationRule("noOverlap", (value) => {
      const parsedEnd = parse(value, "h:mm aa", new Date());

      for (let day in hours) {
        const intervals = hours[day];
        for (let i = 0; i < intervals.length; i++) {
          const thisEnd = intervals[i].endTime;
          if (thisEnd && format(thisEnd, "h:mm aa") === value) {
            const currentStart = intervals[i].startTime;
            const currentEnd = parsedEnd;

            for (let j = 0; j < intervals.length; j++) {
              if (i === j) continue;
              const o = intervals[j].startTime;
              const c = intervals[j].endTime;
              if (o && c) {
                if (
                  (isAfter(currentStart, o) && isBefore(currentStart, c)) ||
                  (isAfter(currentEnd, o) && isBefore(currentEnd, c)) ||
                  (isBefore(currentStart, o) && isAfter(currentEnd, c)) ||
                  (isEqual(currentStart, o) && isEqual(currentEnd, c))
                ) {
                  return false;
                }
              }
            }
          }
        }
      }

      return true;
    });

    return () => {
      ValidatorForm.removeValidationRule("isAfterStart");
      ValidatorForm.removeValidationRule("noOverlap");
    };
  }, [hours]);

  useEffect(() => {
    if (clinicId) {
      getAvailability(`organizationId=${clinicId}`).then((res) => {});
    }
  }, [clinicId]);

  useEffect(() => {
    if (doctorId) {
      getAvailability(`userId=${doctorId}`).then((res) => {});
    }
  }, [doctorId]);

  useEffect(() => {
    if (availability && availability?.length) {
      const convertFormData = availability.reduce((acc, item) => {
        const day = item.dayOfWeek;
        const startTime = new Date(item.openingTime); //moment(parseInt(item.openingTime)).format("HH:mm");
        const endTime = new Date(item.closingTime); //moment(parseInt(item.closingTime)).format("HH:mm");
        const slotType = item.slotType === "online";

        if (!acc[day]) {
          acc[day] = [];
        }

        acc[day].push({ startTime, endTime, slotType });

        return acc;
      }, {});

      setHours(convertFormData);
    }
  }, [availability]);

  const handleTimeChange = (day, index, type, date) => {
    setHours((prev) => {
      const dayIntervals = [...prev[day]];
      dayIntervals[index] = { ...dayIntervals[index], [type]: date };
      return { ...prev, [day]: dayIntervals };
    });
  };

  const handleSlotChange = (day, index, type, slotTypeValue) => {
    setHours((prev) => {
      const dayIntervals = [...prev[day]];
      dayIntervals[index] = { ...dayIntervals[index], [type]: slotTypeValue };
      return { ...prev, [day]: dayIntervals };
    });
  };

  const addInterval = (day) => {
    setHours((prev) => {
      const dayIntervals = [
        ...prev[day],
        { startTime: null, endTime: null, slotType: false },
      ];
      return { ...prev, [day]: dayIntervals };
    });
  };

  const removeInterval = (day, index) => {
    setHours((prev) => {
      const dayIntervals = prev[day].filter((_, i) => i !== index);
      return {
        ...prev,
        [day]:
          dayIntervals.length > 0
            ? dayIntervals
            : [{ startTime: null, endTime: null, slotType: false }],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (clinicId || doctorId) {
      let payload = {
        availability: Object.assign({}, hours),
      };
      if (clinicId) {
        payload.clinicId = clinicId;
      } else if (doctorId) {
        payload.doctorId = doctorId;
      } else {
      }

      const convertedPayload = {
        ...payload,
        availability: Object.fromEntries(
          Object.entries(payload.availability).map(([day, slots]) => [
            day,
            slots.map(({ startTime, endTime, slotType }) => ({
              startTime: startTime ? new Date(startTime).getTime() : null,
              endTime: endTime ? new Date(endTime).getTime() : null,
              slotType: slotType ? "online" : "offline",
            })),
          ])
        ),
      };

      const response = await saveAvailability(
        convertedPayload,
        availability?.length > 0
      );
    }
  };

  // Custom input for DatePicker with TextValidator and MUI v4 TextField
  const DatePickerInput = React.forwardRef(
    ({ value, onClick, label, validators, errorMessages }, ref) => (
      <TextValidator
        fullWidth
        size="small"
        label={label}
        value={value}
        onClick={onClick}
        inputRef={ref}
        validators={validators}
        errorMessages={errorMessages}
        variant="outlined"
        style={{ marginRight: 10, cursor: "pointer" }}
        InputProps={{ readOnly: true }}
      />
    )
  );

  const repeatTimeForOtherDays = (index) => {
    setHours((prevHours) => {
      const updatedHours = { ...prevHours };
      const mondaySlot = updatedHours["Monday"][index]; // Get the interval you clicked

      Object.keys(updatedHours).forEach((day) => {
        if (day !== "Monday") {
          // If other days have fewer intervals, create empty slots
          while (updatedHours[day].length <= index) {
            updatedHours[day].push({
              startTime: null,
              endTime: null,
              slotType: false,
            });
          }

          // Copy this specific interval to same position
          updatedHours[day][index] = { ...mondaySlot };
        }
      });

      return updatedHours;
    });
  };

  return (
    <Box p={3}>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography variant="h5" gutterBottom>
              {mode === "doctor"
                ? "Set Doctor Available Hours (Multiple Intervals Per Day)"
                : "Set Clinic Opening Hours (Multiple Intervals Per Day)"}{" "}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            {daysOfWeek.map((day, dayIndex) => (
              <Grid
                key={day}
                container
                spacing={1}
                style={{ marginBottom: "16px" }}
              >
                <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                  <Typography variant="h6">{day}</Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={10} lg={10} xl={10}>
                  {hours[day]?.map((interval, index) => (
                    <Grid key={index} container spacing={2}>
                      <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                        <DatePicker
                          selected={interval.startTime}
                          onChange={(date) =>
                            handleTimeChange(day, index, "startTime", date)
                          }
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="Start Time"
                          dateFormat="HH:mm"
                          timeFormat="HH:mm"
                          placeholderText="Start Time"
                          customInput={
                            <DatePickerInput
                              label="Start Time"
                              // validators={['required']}
                              // errorMessages={['Start time is required']}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                        <DatePicker
                          selected={interval.endTime}
                          onChange={(date) =>
                            handleTimeChange(day, index, "endTime", date)
                          }
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="End Time"
                          dateFormat="HH:mm"
                          timeFormat="HH:mm"
                          placeholderText="End Time"
                          customInput={
                            <DatePickerInput
                              label="End Time"
                              validators={[
                                // 'required',
                                "isAfterStart",
                                "noOverlap",
                              ]}
                              errorMessages={[
                                // 'End time is required',
                                "End time must be after start time",
                                "Intervals cannot overlap",
                              ]}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        {!clinicId && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                checked={interval.slotType}
                                onChange={() =>
                                  handleSlotChange(
                                    day,
                                    index,
                                    "slotType",
                                    !interval.slotType
                                  )
                                }
                              />
                            }
                            label="Is Online?"
                            labelPlacement="end"
                          />
                        )}
                        <IconButton
                          aria-label="remove interval"
                          onClick={() => removeInterval(day, index)}
                          disabled={hours[day].length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {dayIndex === 0 && (
                          <Button
                            style={{ textTransform: "capitalize" }}
                            variant="outlined"
                            onClick={() => repeatTimeForOtherDays(index)}
                            size="small"
                          >
                            Repeat for other days
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addInterval(day)}
                  >
                    Add Interval
                  </Button>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Button
              variant="contained"
              color="primary"
              disableElevation={true}
              type="submit"
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </ValidatorForm>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    availability: state.departments.availability,
  };
};

const mapDispatchToProp = (dispatch) => {
  return {
    getAvailability: (id) => {
      return dispatch(getAvailabilityRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    saveAvailability: (data, isUpdate) => {
      return dispatch(saveAvailabilityRequest(data, isUpdate)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProp)(ClinicTiming);
